import { useContext, useState, useEffect } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";

import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid";
import MUIBox from "@mui/material/Box";
import MUIFormGroup from "@mui/material/FormGroup";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUICheckbox from "@mui/material/Checkbox";
import MUIFormControl from "@mui/material/FormControl";
import MUIFormLabel from "@mui/material/FormLabel";
import MUITextField from "@mui/material/TextField";
import MUIRadioGroup from "@mui/material/RadioGroup";
import MUIRadio from "@mui/material/Radio";
import MUIDivider from "@mui/material/Divider";
import MUITypography from "@mui/material/Typography";
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUIHomeIcon from "@mui/icons-material/Home";

import Card from "../../Card";
import OrderActivity from "../../../Utility/Crud/OrderActivity";

/**
 * Home Assessment Card renders a Card component displaying home assessment information
 * @returns {JSX.Element}
 */
const className = "CardChartHomeAssessment";
const title = "Home Assessment";
const icon = MUIHomeIcon;

export default function CardChartHomeAssessment(props) {
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);

  const [editMode, setEditMode] = useState(props.startInEditMode ?? false);
  const [cardChart, setCardChart] = useState(null);

  // Derive equipment type from context data (not state, as it's purely computed)
  const deliveryInformation =
    Object.values(chartContext.chart?.deliveryInformation || {})
      .sort((a, b) => b.id - a.id)
      .find(
        (d) =>
          d &&
          d.orderId === chartContext.orderId &&
          (d.documentType === "Home Assessment" ||
            d.documentType === "Home Assessment Web") &&
          d.documentInfoPruned &&
          !d.documentInfoPruned.includes("Home Assessment Completed") &&
          d.deleted === false,
      ) || {};

  const target = "deliveryInformation";
  const targetId = deliveryInformation?.id;
  const parentOrder =
    OrderActivity.getParentOrder(chartContext.orderId, chartContext.chart) ||
    null;
  const parentOrderProducts = OrderActivity.getProducts(
    parentOrder,
    chartContext.chart,
  );
  const primaryProduct = OrderActivity.getPrimaryProduct(parentOrderProducts);
  const productType =
    mainContext.productTypesIndexed[primaryProduct?.productTypeId];
  const equipmentType = productType?.id === 11 ? "POV" : "PWC";
  const isPovOrder = equipmentType === "POV";

  const defaultHomeAssessmentDocument = {
    adequateIngressRegress: null,
    adequateSpacePOV: isPovOrder ? null : true,
    adequateSpacePWC: isPovOrder ? true : null,
    bathroomDimensionX: "",
    bathroomDimensionY: "",
    bathroomThresholdWidth: "",
    bedroomDimensionX: "",
    bedroomDimensionY: "",
    bedroomThresholdWidth: "",
    carpet: false,
    company: "uat",
    covidVirus: false,
    flooringComments: "",
    flooringMobilityAppropriate: null,
    flooringOther: "",
    hallwaysDimensionX: "",
    hallwaysDimensionY: "",
    hallwaysThresholdWidth: "",
    inadequateIngressRegress: "",
    inadequateSpacePOVDescription: "",
    inadequateSpacePWCDescription: "",
    isNinetyThresholdPOV: false,
    isNinetyThresholdPWC: false,
    isOtherPOV: isPovOrder ? null : false,
    isOtherPWC: isPovOrder ? false : null,
    kitchenDimensionX: "",
    kitchenDimensionY: "",
    kitchenThresholdWidth: "",
    linoleum: false,
    livingRoomDimensionX: "",
    livingRoomDimensionY: "",
    livingRoomThresholdWidth: "",
    ninetyThresholdPOV: "",
    ninetyThresholdPWC: "",
    orderId: chartContext.orderId,
    otherPOV: "",
    otherPWC: "",
    patientSignedDate: null,
    patientSignedDateRef: "",
    representativeSignedDate: null,
    representativeSignedDateRef: "",
    tile: false,
    typeOfAssessment: null,
    woodLaminate: false,
  };

  const parsedHomeAssessmentDocument = deliveryInformation?.documentInfoPruned
    ? JSON.parse(deliveryInformation.documentInfoPruned)
    : {};

  const homeAssessmentDocument =
    Object.keys(parsedHomeAssessmentDocument).length === 0
      ? defaultHomeAssessmentDocument
      : parsedHomeAssessmentDocument;

  // Close edit mode if originalChart changes (on save)
  useEffect(() => {
    setEditMode(true);
    setCardChart(null);
  }, [chartContext.originalChart]);

  // Sync this card's edit mode with ChartContext for ChartNavigationBar display
  useEffect(() => {
    chartContext.setCardEditMode?.(className, editMode);
    return () => chartContext.setCardEditMode?.(className, false);
  }, [editMode, chartContext.setCardEditMode]);

  /**
   * Builds a new home assessment entry if it doesn't exist.
   */
  useEffect(() => {
    if (!deliveryInformation.id && editMode) {
      // Build new home assessment entry if it doesn't exist
      const newDeliveryInformationId = chartContext.getNegativeId();
      chartContext.onChange({
        deliveryInformation: {
          [newDeliveryInformationId]: {
            id: newDeliveryInformationId,
            orderId: chartContext.orderId,
            documentType: "Home Assessment Web",
            documentInfoPruned: JSON.stringify(defaultHomeAssessmentDocument),
            uploadedByUserprofileId: mainContext.currentUser.id,
            uploadedOn: new Date().toISOString(),
            deleted: false,
          },
        },
      });
    }
  }, [deliveryInformation.id, editMode]);

  const updateHomeAssessmentDocument = (updates) => {
    const updatedDocument = { ...homeAssessmentDocument, ...updates };

    if (deliveryInformation?.id) {
      chartContext.onChange({
        deliveryInformation: {
          [deliveryInformation.id]: {
            documentInfoPruned: JSON.stringify(updatedDocument),
          },
        },
      });
    }
  };

  /**
   * Sets up validation rules when in edit mode for ordering provider not found.
   */
  useEffect(() => {
    if (editMode && chartContext.chart[target][targetId]?.documentInfoPruned) {
      chartContext.updateValidationRules({
        key: className,
        rules: [
          {
            target: target,
            id: targetId,
            field: "documentInfoPruned",
            rule: {
              type: "custom",
              function: (entity) => {
                const data = JSON.parse(entity || "{}");
                const errors = {};
                if (
                  !data.carpet &&
                  !data.tile &&
                  !data.woodLaminate &&
                  !data.linoleum &&
                  !data.other
                ) {
                  errors.flooringType =
                    "At least one flooring type is required.";
                }
                if (data.other && !data.flooringOther) {
                  errors.flooringOther = "Required.";
                }

                if (data.flooringMobilityAppropriate === null) {
                  errors.flooringMobilityAppropriate = "Required.";
                }
                if (
                  (data.flooringMobilityAppropriate === false ||
                    data.flooringMobilityAppropriate === null) &&
                  !data.flooringComments
                ) {
                  errors.flooringComments = "Required.";
                }

                if (data.adequateSpacePOV === null) {
                  errors.adequateSpacePOV = "Required.";
                }
                if (
                  data.adequateSpacePOV === false &&
                  !data.inadequateSpacePOVDescription
                ) {
                  errors.inadequateSpacePOVDescription = "Required.";
                }

                if (data.isOtherPOV === null) {
                  errors.isOtherPOV = "Required.";
                }
                if (data.isOtherPOV === true && !data.otherPOV) {
                  errors.otherPOV = "Required.";
                }

                if (data.adequateSpacePWC === null) {
                  errors.adequateSpacePWC = "Required.";
                }
                if (
                  data.adequateSpacePWC === false &&
                  !data.inadequateSpacePWCDescription
                ) {
                  errors.inadequateSpacePWCDescription = "Required.";
                }

                if (data.isOtherPWC === null) {
                  errors.isOtherPWC = "Required.";
                }
                if (data.isOtherPWC === true && !data.otherPWC) {
                  errors.otherPWC = "Required.";
                }

                if (
                  data.adequateIngressRegress === false &&
                  !data.inadequateIngressRegress
                ) {
                  errors.inadequateIngressRegress = "Required.";
                }
                if (data.adequateIngressRegress === null) {
                  errors.adequateIngressRegress = "Required.";
                }
                return Object.keys(errors).length > 0 ? errors : true;
              },
            },
          },
        ],
      });
    } else {
      chartContext.updateValidationRules({ key: className, rules: [] });
    }
  }, [editMode, chartContext.chart[target][targetId]?.documentInfoPruned]);

  /**
   * Returns the styles for a container that should be full width at a given max width.
   * Only used when sidebar=true to handle responsive container queries.
   * @param {number} maxWidth - The max width at which the container should be full width.
   * @returns {Object} The styles for the container.
   */
  const getContainerQuery = (maxWidth) => {
    if (!props.sidebar) {
      return {};
    }

    return {
      [`@container (max-width: ${maxWidth}px)`]: {
        flexBasis: "100% !important",
        maxWidth: "100% !important",
      },
    };
  };

  const renderFlooringTypeSection = () => {
    return (
      <MUIGrid item xs={12} sx={getContainerQuery(600)}>
        <MUIGrid container spacing={2}>
          <MUIGrid item xs={12} md={6} lg={4} sx={getContainerQuery(600)}>
            <MUIFormControl
              component="fieldset"
              variant="standard"
              required
              error={
                !!chartContext.validationIssues[className]?.[target]?.[targetId]
                  ?.documentInfoPruned?.flooringType
              }
            >
              <MUIFormLabel
                component="legend"
                sx={{
                  mb: 0,
                  fontSize: ".75rem",
                }}
              >
                Flooring Type
              </MUIFormLabel>
              <MUIFormGroup sx={{ "& .MuiFormControlLabel-root": { mb: -1 } }}>
                <MUIFormControlLabel
                  control={
                    <MUICheckbox
                      size="small"
                      checked={homeAssessmentDocument?.carpet || false}
                      onChange={(e) => {
                        updateHomeAssessmentDocument({
                          carpet: e.target.checked,
                        });
                        setCardChart({
                          ...cardChart,
                          carpet: e.target.checked,
                        });
                      }}
                    />
                  }
                  label="Carpet"
                />
                <MUIFormControlLabel
                  value="tile"
                  control={
                    <MUICheckbox
                      size="small"
                      checked={homeAssessmentDocument?.tile || false}
                      onChange={(e) => {
                        updateHomeAssessmentDocument({
                          tile: e.target.checked,
                        });
                        setCardChart({ ...cardChart, tile: e.target.checked });
                      }}
                    />
                  }
                  label="Tile"
                />
                <MUIFormControlLabel
                  value="woodLaminate"
                  control={
                    <MUICheckbox
                      size="small"
                      checked={homeAssessmentDocument?.woodLaminate || false}
                      onChange={(e) => {
                        updateHomeAssessmentDocument({
                          woodLaminate: e.target.checked,
                        });
                        setCardChart({
                          ...cardChart,
                          woodLaminate: e.target.checked,
                        });
                      }}
                    />
                  }
                  label="Wood/Laminate"
                />
                <MUIFormControlLabel
                  control={
                    <MUICheckbox
                      size="small"
                      checked={homeAssessmentDocument?.linoleum || false}
                      onChange={(e) => {
                        updateHomeAssessmentDocument({
                          linoleum: e.target.checked,
                        });
                        setCardChart({
                          ...cardChart,
                          linoleum: e.target.checked,
                        });
                      }}
                    />
                  }
                  label="Linoleum"
                  value="linoleum"
                />
                <MUIFormControlLabel
                  control={
                    <MUICheckbox
                      size="small"
                      checked={homeAssessmentDocument?.other || false}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const updates = { other: isChecked };

                        if (!isChecked) {
                          updates.flooringOther = "";
                        }

                        updateHomeAssessmentDocument(updates);
                        setCardChart({ ...cardChart, ...updates });
                      }}
                    />
                  }
                  label="Other"
                  value="other"
                />
              </MUIFormGroup>
            </MUIFormControl>
            {homeAssessmentDocument.other && (
              <MUITextField
                sx={{ mt: 2 }}
                value={homeAssessmentDocument.flooringOther || ""}
                label="Other Flooring Type(s)"
                size="small"
                fullWidth={true}
                variant="standard"
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { resize: "vertical" } }}
                onChange={(e) => {
                  updateHomeAssessmentDocument({
                    flooringOther: e.target.value,
                  });
                  setCardChart({ ...cardChart, flooringOther: e.target.value });
                }}
                required={true}
                error={
                  !!chartContext.validationIssues[className]?.[target]?.[
                    targetId
                  ]?.documentInfoPruned?.flooringOther
                }
                helperText={
                  chartContext.validationIssues[className]?.[target]?.[targetId]
                    ?.documentInfoPruned?.flooringOther
                }
              />
            )}
          </MUIGrid>
          <MUIGrid item xs={12} md={6} lg={4} sx={getContainerQuery(600)}>
            <MUIFormControl
              component="fieldset"
              variant="standard"
              required
              error={
                !!chartContext.validationIssues[className]?.[target]?.[targetId]
                  ?.documentInfoPruned?.flooringMobilityAppropriate
              }
            >
              <MUIFormLabel
                component="legend"
                sx={{
                  mb: 0,
                  fontSize: ".75rem",
                }}
              >
                Flooring suitable for power mobility device?
              </MUIFormLabel>
              <MUIRadioGroup
                row
                value={
                  homeAssessmentDocument.flooringMobilityAppropriate === null
                    ? ""
                    : homeAssessmentDocument.flooringMobilityAppropriate
                      ? "yes"
                      : "no"
                }
                onChange={(e) => {
                  const isSuitable = e.target.value === "yes";
                  const updates = { flooringMobilityAppropriate: isSuitable };

                  if (isSuitable) {
                    updates.flooringComments = "";
                  }
                  updateHomeAssessmentDocument(updates);
                  setCardChart({
                    ...cardChart,
                    flooringMobilityAppropriate: isSuitable,
                  });
                }}
              >
                <MUIFormControlLabel
                  value="yes"
                  control={<MUIRadio size="small" />}
                  label="Yes"
                />
                <MUIFormControlLabel
                  value="no"
                  control={<MUIRadio size="small" />}
                  label="No"
                />
              </MUIRadioGroup>
            </MUIFormControl>
            {homeAssessmentDocument.flooringMobilityAppropriate === false && (
              <MUITextField
                label="Reason"
                size="small"
                fullWidth={true}
                variant="standard"
                InputLabelProps={{ shrink: true }}
                value={homeAssessmentDocument.flooringComments || ""}
                onChange={(e) => {
                  updateHomeAssessmentDocument({
                    flooringComments: e.target.value,
                  });
                  setCardChart({
                    ...cardChart,
                    flooringComments: e.target.value,
                  });
                }}
                required={true}
                error={
                  !!chartContext.validationIssues[className]?.[target]?.[
                    targetId
                  ]?.documentInfoPruned?.flooringComments
                }
                helperText={
                  chartContext.validationIssues[className]?.[target]?.[targetId]
                    ?.documentInfoPruned?.flooringComments
                }
              />
            )}
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  const renderMeasurementsSection = () => {
    const sections = [
      { label: "Kitchen/Food Prep", key: "kitchen" },
      { label: "Bedroom/Sleeping", key: "bedroom" },
      { label: "Bathroom", key: "bathroom" },
      { label: "Living Room", key: "livingRoom" },
      { label: "Connecting Hallways", key: "hallways" },
    ];

    return (
      <MUIGrid item xs={12} sx={getContainerQuery(600)}>
        <MUIGrid container spacing={2} rowSpacing={1}>
          {/* Header Row */}
          <MUIGrid item xs={12} sx={getContainerQuery(600)}>
            <MUIGrid container spacing={4} rowSpacing={1}>
              <MUIGrid
                item
                lg={props.sidebar ? 4 : 3}
                md={props.sidebar ? 12 : 3}
                xs={12}
              >
                {/* Empty space for room labels */}
              </MUIGrid>
              <MUIGrid
                item
                lg={props.sidebar ? 2.5 : 2}
                md={props.sidebar ? 3.5 : 2}
                sm={props.sidebar ? 12 : 3.5}
                xs={3.5}
              >
                <MUITypography
                  variant="caption"
                  sx={{ textAlign: "right", pr: 4, whiteSpace: "nowrap" }}
                >
                  Threshold Width
                </MUITypography>
              </MUIGrid>
              <MUIGrid
                item
                lg={props.sidebar ? 2.5 : 2}
                md={props.sidebar ? 3.5 : 2}
                sm={props.sidebar ? 12 : 3.5}
                xs={3.5}
              >
                <MUITypography
                  variant="caption"
                  sx={{ textAlign: "right", pr: 3, whiteSpace: "nowrap" }}
                >
                  Room Dimensions
                </MUITypography>
              </MUIGrid>
              <MUIGrid
                item
                lg="auto"
                md={props.sidebar ? 1 : 0.5}
                sm={props.sidebar ? 12 : "auto"}
                xs={"auto"}
              >
                {/* Empty space for 'x' */}
              </MUIGrid>
              <MUIGrid
                item
                lg={props.sidebar ? 2.5 : 2}
                md={props.sidebar ? 3.5 : 2}
                sm={props.sidebar ? 12 : 3.5}
                xs={3.5}
              >
                {/* Empty space for second dimension */}
              </MUIGrid>
            </MUIGrid>
          </MUIGrid>
          {/* Data Rows */}
          {sections.map((section) => (
            <MUIGrid item xs={12} sx={getContainerQuery(600)} key={section.key}>
              <MUIGrid container spacing={4} rowSpacing={1}>
                <MUIGrid
                  item
                  lg={props.sidebar ? 4 : 3}
                  md={props.sidebar ? 12 : 3}
                  xs={12}
                >
                  <MUITypography
                    variant="subtitle2"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {section.label}
                  </MUITypography>
                </MUIGrid>
                <MUIGrid
                  item
                  lg={props.sidebar ? 2.5 : 2}
                  md={props.sidebar ? 3.5 : 2}
                  sm={props.sidebar ? 12 : 3.5}
                  xs={3.5}
                >
                  <MUITextField
                    size="small"
                    variant="standard"
                    InputProps={{
                      endAdornment: (
                        <MUIInputAdornment position="end" sx={{ ml: 1 }}>
                          in
                        </MUIInputAdornment>
                      ),
                    }}
                    inputProps={{
                      style: { textAlign: "right" },
                    }}
                    value={
                      homeAssessmentDocument[`${section.key}ThresholdWidth`] !==
                      "0"
                        ? homeAssessmentDocument[`${section.key}ThresholdWidth`]
                        : ""
                    }
                    onChange={(e) => {
                      let value = e.target.value;

                      // Allow only valid numbers (integers or decimals)
                      const validPattern = /^\d*\.?\d*$/;
                      if (!validPattern.test(value)) {
                        value = value.match(/^\d*\.?\d*/)?.[0] || "";
                        e.target.value = value;
                      }
                      updateHomeAssessmentDocument({
                        [`${section.key}ThresholdWidth`]: value,
                      });
                      setCardChart({
                        ...cardChart,
                        [`${section.key}ThresholdWidth`]: value,
                      });
                    }}
                  />
                </MUIGrid>
                <MUIGrid
                  item
                  lg={props.sidebar ? 2.5 : 2}
                  md={props.sidebar ? 3.5 : 2}
                  sm={props.sidebar ? 12 : 3.5}
                  xs={3.5}
                >
                  <MUITextField
                    size="small"
                    variant="standard"
                    InputProps={{
                      endAdornment: (
                        <MUIInputAdornment position="end" sx={{ ml: 1 }}>
                          ft
                        </MUIInputAdornment>
                      ),
                    }}
                    inputProps={{
                      style: { textAlign: "right" },
                    }}
                    value={
                      homeAssessmentDocument[`${section.key}DimensionX`] !== "0"
                        ? homeAssessmentDocument[`${section.key}DimensionX`]
                        : ""
                    }
                    onChange={(e) => {
                      let value = e.target.value;

                      // Allow only valid numbers (integers or decimals)
                      const validPattern = /^\d*\.?\d*$/;
                      if (!validPattern.test(value)) {
                        value = value.match(/^\d*\.?\d*/)?.[0] || "";
                        e.target.value = value;
                      }
                      updateHomeAssessmentDocument({
                        [`${section.key}DimensionX`]: value,
                      });
                      setCardChart({
                        ...cardChart,
                        [`${section.key}DimensionX`]: value,
                      });
                    }}
                  />
                </MUIGrid>
                <MUIGrid
                  item
                  lg="auto"
                  md={props.sidebar ? 1 : 0.5}
                  sm={props.sidebar ? 12 : "auto"}
                  xs={"auto"}
                  sx={{
                    display: "flex",
                    alignItems: "end",
                    justifyContent: { xs: "flex-end", sm: "flex-end" },
                    textAlign: "right",
                  }}
                >
                  <MUITypography variant="body2">x</MUITypography>
                </MUIGrid>
                <MUIGrid
                  item
                  lg={props.sidebar ? 2.5 : 2}
                  md={props.sidebar ? 3.5 : 2}
                  sm={props.sidebar ? 12 : 3.5}
                  xs={3.5}
                >
                  <MUITextField
                    size="small"
                    variant="standard"
                    InputProps={{
                      endAdornment: (
                        <MUIInputAdornment position="end" sx={{ ml: 1 }}>
                          ft
                        </MUIInputAdornment>
                      ),
                    }}
                    inputProps={{
                      style: { textAlign: "right" },
                    }}
                    value={
                      homeAssessmentDocument[`${section.key}DimensionY`] !== "0"
                        ? homeAssessmentDocument[`${section.key}DimensionY`]
                        : ""
                    }
                    onChange={(e) => {
                      let value = e.target.value;

                      // Allow only valid numbers (integers or decimals)
                      const validPattern = /^\d*\.?\d*$/;
                      if (!validPattern.test(value)) {
                        value = value.match(/^\d*\.?\d*/)?.[0] || "";
                        e.target.value = value;
                      }
                      updateHomeAssessmentDocument({
                        [`${section.key}DimensionY`]: value,
                      });
                      setCardChart({
                        ...cardChart,
                        [`${section.key}DimensionY`]: value,
                      });
                    }}
                  />
                </MUIGrid>
              </MUIGrid>
            </MUIGrid>
          ))}
        </MUIGrid>
      </MUIGrid>
    );
  };

  const renderAdditionalInformationSection = () => {
    const renderYesNoSection = ({
      label,
      valueKey,
      descriptionKey,
      descriptionLabel,
      validationKey,
      descriptionValidationKey,
      otherLabel,
      otherValueKey,
      otherValidationKey,
    }) => {
      return (
        <>
          <MUIGrid item xs={12} sx={getContainerQuery(600)}>
            <MUIGrid container spacing={1}>
              <MUIGrid item xs={12} md={4} lg={3} sx={getContainerQuery(600)}>
                <MUIFormControl
                  component="fieldset"
                  variant="standard"
                  required
                >
                  <MUIFormLabel
                    component="legend"
                    sx={{ mb: 0, fontSize: ".75rem" }}
                    error={
                      !!chartContext.validationIssues[className]?.[target]?.[
                        targetId
                      ]?.documentInfoPruned?.[validationKey]
                    }
                  >
                    {label}
                  </MUIFormLabel>
                  <MUIRadioGroup
                    row
                    value={
                      homeAssessmentDocument[valueKey] === null
                        ? ""
                        : homeAssessmentDocument[valueKey]
                          ? "yes"
                          : "no"
                    }
                    onChange={(e) => {
                      const isAdequate = e.target.value === "yes";
                      const updates = { [valueKey]: isAdequate };

                      if (isAdequate && descriptionKey) {
                        updates[descriptionKey] = "";
                      }
                      updateHomeAssessmentDocument(updates);
                      setCardChart({ ...cardChart, ...updates });
                    }}
                  >
                    <MUIFormControlLabel
                      value="yes"
                      control={<MUIRadio size="small" />}
                      label="Yes"
                    />
                    <MUIFormControlLabel
                      value="no"
                      control={<MUIRadio size="small" />}
                      label="No"
                    />
                  </MUIRadioGroup>
                </MUIFormControl>
              </MUIGrid>
              {homeAssessmentDocument[valueKey] === false && descriptionKey && (
                <MUIGrid
                  item
                  xs={12}
                  md={8}
                  lg={4}
                  sx={{ ...getContainerQuery(600) }}
                >
                  <MUITextField
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    variant="standard"
                    label={descriptionLabel}
                    fullWidth
                    required={true}
                    value={homeAssessmentDocument[descriptionKey] || ""}
                    onChange={(e) => {
                      updateHomeAssessmentDocument({
                        [descriptionKey]: e.target.value,
                      });
                      setCardChart({
                        ...cardChart,
                        [descriptionKey]: e.target.value,
                      });
                    }}
                    error={
                      !!chartContext.validationIssues[className]?.[target]?.[
                        targetId
                      ]?.documentInfoPruned?.[descriptionValidationKey]
                    }
                    helperText={
                      chartContext.validationIssues[className]?.[target]?.[
                        targetId
                      ]?.documentInfoPruned?.[descriptionValidationKey]
                    }
                  />
                </MUIGrid>
              )}
            </MUIGrid>
          </MUIGrid>
          {otherLabel && (
            <MUIGrid item xs={12} sx={getContainerQuery(600)}>
              <MUIGrid container spacing={1}>
                <MUIGrid item xs={12} md={4} lg={3} sx={getContainerQuery(600)}>
                  <MUIFormControl
                    component="fieldset"
                    variant="standard"
                    required
                  >
                    <MUIFormLabel
                      component="legend"
                      sx={{ mb: 0, fontSize: ".75rem" }}
                      error={
                        !!chartContext.validationIssues[className]?.[target]?.[
                          targetId
                        ]?.documentInfoPruned?.[otherValidationKey]
                      }
                    >
                      {otherLabel}
                    </MUIFormLabel>
                    <MUIRadioGroup
                      row
                      value={
                        homeAssessmentDocument[otherValueKey] === null
                          ? ""
                          : homeAssessmentDocument[otherValueKey]
                            ? "yes"
                            : "no"
                      }
                      onChange={(e) => {
                        const isOtherIssues = e.target.value === "yes";
                        const updates = { [otherValueKey]: isOtherIssues };

                        if (!isOtherIssues) {
                          updates[
                            otherValueKey === "isOtherPOV"
                              ? "otherPOV"
                              : "otherPWC"
                          ] = "";
                        }

                        updateHomeAssessmentDocument(updates);
                        setCardChart({ ...cardChart, ...updates });
                      }}
                    >
                      <MUIFormControlLabel
                        value="yes"
                        control={<MUIRadio size="small" />}
                        label="Yes"
                      />
                      <MUIFormControlLabel
                        value="no"
                        control={<MUIRadio size="small" />}
                        label="No"
                      />
                    </MUIRadioGroup>
                  </MUIFormControl>
                </MUIGrid>
                {homeAssessmentDocument[otherValueKey] === true && (
                  <MUIGrid
                    item
                    xs={12}
                    md={8}
                    lg={4}
                    sx={{ ...getContainerQuery(600) }}
                  >
                    <MUITextField
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      variant="standard"
                      label="Describe"
                      fullWidth
                      value={
                        homeAssessmentDocument[
                          otherValueKey === "isOtherPOV"
                            ? "otherPOV"
                            : "otherPWC"
                        ] || ""
                      }
                      onChange={(e) => {
                        const fieldKey =
                          otherValueKey === "isOtherPOV"
                            ? "otherPOV"
                            : "otherPWC";
                        updateHomeAssessmentDocument({
                          [fieldKey]: e.target.value,
                        });
                        setCardChart({
                          ...cardChart,
                          [fieldKey]: e.target.value,
                        });
                      }}
                      required={true}
                      error={
                        !!chartContext.validationIssues[className]?.[target]?.[
                          targetId
                        ]?.documentInfoPruned?.[
                          otherValueKey === "isOtherPOV"
                            ? "otherPOV"
                            : "otherPWC"
                        ]
                      }
                      helperText={
                        chartContext.validationIssues[className]?.[target]?.[
                          targetId
                        ]?.documentInfoPruned?.[
                          otherValueKey === "isOtherPOV"
                            ? "otherPOV"
                            : "otherPWC"
                        ]
                      }
                    />
                  </MUIGrid>
                )}
              </MUIGrid>
            </MUIGrid>
          )}
        </>
      );
    };

    return (
      <MUIGrid item xs={12} sx={getContainerQuery(600)}>
        <MUIGrid container spacing={2}>
          {isPovOrder
            ? renderYesNoSection({
                label: "Adequate Access for POV/Scooter?",
                valueKey: "adequateSpacePOV",
                descriptionKey: "inadequateSpacePOVDescription",
                descriptionLabel: "Describe",
                validationKey: "adequateSpacePOV",
                descriptionValidationKey: "inadequateSpacePOVDescription",
                otherLabel: "Other Issues?",
                otherValueKey: "isOtherPOV",
                otherValidationKey: "isOtherPOV",
              })
            : renderYesNoSection({
                label: "Adequate Access for Power Wheelchair?",
                valueKey: "adequateSpacePWC",
                descriptionKey: "inadequateSpacePWCDescription",
                descriptionLabel: "Describe",
                validationKey: "adequateSpacePWC",
                descriptionValidationKey: "inadequateSpacePWCDescription",
                otherLabel: "Other Issues?",
                otherValueKey: "isOtherPWC",
                otherValidationKey: "isOtherPWC",
              })}
          <MUIGrid item xs={12} sx={getContainerQuery(600)}>
            <MUIGrid container spacing={1}>
              <MUIGrid item xs={12} md={4} lg={3} sx={getContainerQuery(600)}>
                <MUIFormControl
                  component="fieldset"
                  variant="standard"
                  required
                >
                  <MUIFormLabel
                    component="legend"
                    sx={{ mb: 0, fontSize: ".75rem" }}
                    error={
                      !!chartContext.validationIssues[className]?.[target]?.[
                        targetId
                      ]?.documentInfoPruned?.adequateIngressRegress
                    }
                  >
                    Adequate ingress/regress?
                  </MUIFormLabel>
                  <MUIRadioGroup
                    row
                    value={
                      homeAssessmentDocument.adequateIngressRegress === null
                        ? ""
                        : homeAssessmentDocument.adequateIngressRegress
                          ? "yes"
                          : "no"
                    }
                    onChange={(e) => {
                      const isAdequate =
                        e.target.value === "yes" ? true : false;
                      const updates = {
                        adequateIngressRegress: isAdequate ? true : false,
                      };

                      if (!isAdequate) {
                        updates.inadequateIngressRegress = "";
                      }

                      updateHomeAssessmentDocument(updates);
                      setCardChart({ ...cardChart, ...updates });
                      setCardChart({
                        ...cardChart,
                        adequateIngressRegress: isAdequate ? true : false,
                      });
                    }}
                  >
                    <MUIFormControlLabel
                      value="yes"
                      control={<MUIRadio size="small" />}
                      label="Yes"
                    />
                    <MUIFormControlLabel
                      value="no"
                      control={<MUIRadio size="small" />}
                      label="No"
                    />
                  </MUIRadioGroup>
                </MUIFormControl>
              </MUIGrid>
              {homeAssessmentDocument.adequateIngressRegress === false && (
                <MUIGrid
                  item
                  xs={12}
                  md={8}
                  lg={4}
                  sx={{ ...getContainerQuery(600) }}
                >
                  <MUITextField
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    variant="standard"
                    label="Describe"
                    fullWidth
                    required={true}
                    value={
                      homeAssessmentDocument.inadequateIngressRegress || ""
                    }
                    onChange={(e) => {
                      updateHomeAssessmentDocument({
                        inadequateIngressRegress: e.target.value,
                      });
                      setCardChart({
                        ...cardChart,
                        inadequateIngressRegress: e.target.value,
                      });
                    }}
                    error={
                      !!chartContext.validationIssues[className]?.[target]?.[
                        targetId
                      ]?.documentInfoPruned?.inadequateIngressRegress
                    }
                    helperText={
                      chartContext.validationIssues[className]?.[target]?.[
                        targetId
                      ]?.documentInfoPruned?.inadequateIngressRegress
                    }
                  />
                </MUIGrid>
              )}
            </MUIGrid>
          </MUIGrid>
        </MUIGrid>
      </MUIGrid>
    );
  };

  /**
   * Renders the edit view displaying patient approval information.
   * @returns {JSX.Element} Grid layout with patient information display
   */
  const renderEditView = () => {
    return (
      <MUIGrid
        container
        spacing={0}
        rowSpacing={0}
        sx={{ containerType: "inline-size" }}
      >
        {renderFlooringTypeSection()}
        <MUIGrid item xs={12} sx={{ my: 1 }}>
          <MUIDivider sx={{ my: 2 }} orientation="horizontal" flexItem />
        </MUIGrid>
        {renderMeasurementsSection()}
        <MUIGrid item xs={12} sx={{ my: 1 }}>
          <MUIDivider sx={{ my: 2 }} orientation="horizontal" flexItem />
        </MUIGrid>
        {renderAdditionalInformationSection()}
      </MUIGrid>
    );
  };

  /**
   * Renders the read-only view displaying approval information.
   * @returns {JSX.Element} Grid layout with approval information display
   */
  const renderReadOnlyView = () => {
    return <MUIBox></MUIBox>;
  };

  /**
   * Determines which view to render based on edit mode state. Renders search
   * box, add note field, notes table, or empty state.
   * @returns {JSX.Element} Either edit view or read-only view
   */
  const renderContent = () => {
    if (editMode) {
      return renderEditView();
    }
    return renderReadOnlyView();
  };

  /**
   * Returns the action buttons for the card component.
   * @returns {Array} Array of action button components
   */
  const getActions = () => {
    const handleCancel = () => {
      const updatedChart = { ...chartContext.chart };
      const homeAssessmentFields = cardChart ? Object.keys(cardChart) : [];

      const originalDeliveryInformation =
        Object.values(chartContext.originalChart?.deliveryInformation || {})
          .sort((a, b) => b.id - a.id)
          .find(
            (d) =>
              d &&
              d.orderId === chartContext.orderId &&
              (d.documentType === "Home Assessment" ||
                d.documentType === "Home Assessment Web") &&
              !d.documentInfoPruned.includes("Home Assessment Completed"),
          ) || {};

      const originalHomeAssessmentDocument =
        originalDeliveryInformation?.documentInfoPruned
          ? JSON.parse(originalDeliveryInformation.documentInfoPruned)
          : {};

      if (deliveryInformation?.id && deliveryInformation.id > 0) {
        // Revert each changed field back to original
        const revertedDocument = { ...homeAssessmentDocument };
        homeAssessmentFields.forEach((field) => {
          revertedDocument[field] = originalHomeAssessmentDocument[field];
        });

        chartContext.onChange({
          deliveryInformation: {
            [deliveryInformation.id]: {
              documentInfoPruned: JSON.stringify(revertedDocument),
            },
          },
        });
      } else {
        // Remove the new entry by setting it to undefined
        chartContext.onChange({
          deliveryInformation: {
            [deliveryInformation.id]: undefined,
          },
        });
      }

      chartContext.setCardEditMode?.(className, false);
      setEditMode(false);

      const clearedIssues = { ...chartContext.validationIssues };
      delete clearedIssues[className];
      chartContext.updateValidationIssues(clearedIssues);
      setCardChart(null);
    };

    return [
      ...(editMode
        ? [
            ...(!props.startInEditMode
              ? [
                  <MUIButton
                    color="error"
                    disabled={chartContext.isUpdating}
                    onClick={handleCancel}
                    size="small"
                  >
                    Cancel
                  </MUIButton>,
                ]
              : []),
          ]
        : [
            <MUIButton
              disabled={chartContext.isUpdating}
              onClick={() => setEditMode(true)}
              size="small"
            >
              Edit
            </MUIButton>,
          ]),
    ];
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getTitle={() => title}
      getIcon={() => icon}
    />
  );
}

CardChartHomeAssessment.meta = {
  className: className,
  title: title,
  icon: icon,
};
